import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceSync } from "../../hooks/useDebounceSync";
import { createFocusListItems } from "./FocusListItem";
export const KEYBOARD_DIRECTIONS = {
    HORIZONTAL: "horizontal",
    VERTICAL: "vertical",
    ALL: "all"
};
export const useFocusList = (props) => {
    const { listRef, itemSelector = "button:enabled,a,[tabindex]", groupingSelector, keyboardDirection = "all", keyboardEventMustMatchItemSelector, shouldAutoFocus = false, preventScrollOnAutoFocus = false, shouldFocusMatchOnType = true, shouldCirculate = true, focusManagementStrategy = "rovingTabIndex", customFocusMatching, onItemFocus } = props;
    const [focusIndex, setFocusIndex] = useState(0);
    const keyBuffer = useRef("");
    // restore previous FocusIndex on mount
    useEffect(() => {
        if (!listRef.current) {
            return;
        }
        const items = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector);
        const existingIndex = items.findIndex((item) => item.isTabbable);
        const focusIndex = existingIndex === -1 ? 0 : existingIndex;
        setFocusIndex(focusIndex);
    }, [itemSelector, groupingSelector, listRef]);
    useEffect(() => {
        if (!listRef.current) {
            return;
        }
        const items = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector);
        if (focusManagementStrategy === "rovingTabIndex" && (!shouldAutoFocus || groupingSelector)) {
            // TODO: probably replace this 0--- -0-- --0- ---0 logic with e.g. setting initial state in nested component on-init then handle from here
            for (const item of items) {
                item.setTabbable(item.index === focusIndex);
            }
        }
        else if (focusManagementStrategy === "rovingTabIndex") {
            // might not be needed at all, maybe should just respect what is set outside
            for (const item of items) {
                item.setTabbable(false);
            }
        }
    }, [listRef, focusIndex, focusManagementStrategy, itemSelector, shouldAutoFocus, groupingSelector]);
    const clearBufferDebounce = useDebounceSync(useCallback(() => {
        keyBuffer.current = "";
    }, [keyBuffer]), 1000);
    const focusPrev = useCallback(() => {
        if (!listRef?.current) {
            return;
        }
        const items = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector);
        const currentIndex = document.activeElement
            ? items.findIndex((item) => item.has(document.activeElement))
            : -1;
        const newIndex = shouldCirculate ? (currentIndex - 1 + items.length) % items.length : Math.max(currentIndex - 1, 0);
        const item = items[newIndex];
        setFocusIndex(newIndex);
        if (item) {
            item.focus();
            onItemFocus?.();
        }
    }, [listRef, itemSelector, groupingSelector, shouldCirculate, onItemFocus]);
    const focusNext = useCallback(() => {
        if (!listRef?.current) {
            return;
        }
        const items = Array.from(createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector));
        const currentIndex = document.activeElement
            ? items.findIndex((item) => item.has(document.activeElement))
            : -1;
        const lastIndex = Math.max(items.length - 1, 0);
        const newIndex = shouldCirculate ? (currentIndex + 1) % items.length : Math.min(currentIndex + 1, lastIndex);
        const item = items[newIndex];
        setFocusIndex(newIndex);
        if (item) {
            item.focus();
            onItemFocus?.();
        }
    }, [groupingSelector, itemSelector, listRef, onItemFocus, shouldCirculate]);
    const focusFirst = useCallback(() => {
        if (!listRef?.current) {
            return;
        }
        const item = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector)[0];
        if (item) {
            item.focus(preventScrollOnAutoFocus ? { preventScroll: true } : undefined);
            setFocusIndex(0);
            onItemFocus?.();
        }
    }, [groupingSelector, itemSelector, listRef, onItemFocus, preventScrollOnAutoFocus]);
    const focusLast = useCallback(() => {
        if (!listRef?.current) {
            return;
        }
        const items = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector);
        const lastIndex = Math.max(items.length - 1, 0);
        const item = items[lastIndex];
        setFocusIndex(lastIndex);
        if (item) {
            item.focus();
            onItemFocus?.();
        }
    }, [groupingSelector, itemSelector, listRef, onItemFocus]);
    const focusMatching = useCallback((key) => {
        keyBuffer.current += key.toLocaleLowerCase();
        if (customFocusMatching) {
            customFocusMatching(keyBuffer.current);
        }
        else {
            if (!listRef?.current) {
                return;
            }
            const items = listRef.current.querySelectorAll(itemSelector);
            for (const item of createFocusListItems(items, groupingSelector)) {
                const matches = item.matches(keyBuffer.current);
                if (matches) {
                    item.focus();
                    onItemFocus?.();
                    break;
                }
            }
        }
        clearBufferDebounce();
    }, [customFocusMatching, clearBufferDebounce, listRef, itemSelector, groupingSelector, onItemFocus]);
    const handleFocusMatching = useCallback((event) => {
        if (event.key.length === 1 && event.key !== " ") {
            focusMatching(event.key);
        }
    }, [focusMatching]);
    const handleListItemFocus = useCallback((event) => {
        if (listRef.current?.contains(event.target)) {
            const items = createFocusListItems(listRef.current.querySelectorAll(itemSelector), groupingSelector);
            for (const item of items) {
                if (item.has(event.target)) {
                    setFocusIndex(item.index);
                }
            }
        }
    }, [groupingSelector, itemSelector, listRef]);
    const handleListKeyDown = useCallback((event) => {
        if (!listRef.current?.contains(event.target)) {
            // Doesn't seem like we ever need to trigger navigation from outside of the current portalled layer (e.g. in a nested popup)
            return;
        }
        if (keyboardEventMustMatchItemSelector && !event.target.matches?.(itemSelector)) {
            return;
        }
        switch (event.key) {
            case "ArrowDown":
                if (keyboardDirection === KEYBOARD_DIRECTIONS.ALL || keyboardDirection === KEYBOARD_DIRECTIONS.VERTICAL) {
                    event.preventDefault();
                    event.stopPropagation();
                    focusNext();
                }
                break;
            case "ArrowUp":
                if (keyboardDirection === KEYBOARD_DIRECTIONS.ALL || keyboardDirection === KEYBOARD_DIRECTIONS.VERTICAL) {
                    event.preventDefault();
                    event.stopPropagation();
                    focusPrev();
                }
                break;
            case "ArrowRight":
                if (keyboardDirection === KEYBOARD_DIRECTIONS.ALL || keyboardDirection === KEYBOARD_DIRECTIONS.HORIZONTAL) {
                    event.preventDefault();
                    event.stopPropagation();
                    focusNext();
                }
                break;
            case "ArrowLeft":
                if (keyboardDirection === KEYBOARD_DIRECTIONS.ALL || keyboardDirection === KEYBOARD_DIRECTIONS.HORIZONTAL) {
                    event.preventDefault();
                    event.stopPropagation();
                    focusPrev();
                }
                break;
            case "Home":
                event.preventDefault();
                event.stopPropagation();
                focusFirst();
                break;
            case "End":
                event.preventDefault();
                event.stopPropagation();
                focusLast();
                break;
            default:
                shouldFocusMatchOnType && handleFocusMatching(event);
        }
    }, [
        keyboardEventMustMatchItemSelector,
        listRef,
        itemSelector,
        keyboardDirection,
        focusFirst,
        focusLast,
        shouldFocusMatchOnType,
        handleFocusMatching,
        focusNext,
        focusPrev
    ]);
    useEffect(() => {
        let animationFrame;
        if (process.env.NODE_ENV === "test" && shouldAutoFocus) {
            focusFirst();
        }
        if (shouldAutoFocus) {
            // FIXME: Known issue: this or any other on mount logic doesn't re-run if popper is reopened right after focus is restored to the trigger (so while it's still animating)
            animationFrame = requestAnimationFrame(focusFirst);
        }
        return function cleanup() {
            clearBufferDebounce.cancel();
            if (animationFrame !== undefined) {
                window.cancelAnimationFrame(animationFrame);
            }
        };
    }, [clearBufferDebounce, shouldAutoFocus, focusFirst]);
    return {
        handleListKeyDown,
        handleListItemFocus,
        focusPrev,
        focusNext,
        focusFirst,
        focusLast,
        focusMatching,
        handleFocusMatching
    };
};
